import { getLocalStorage, setLocalStorage } from "./common";
import { USER_INFO_KEY } from "../constants/common";
import { apiCall } from "../services/v2/apiService";
import * as endPoints from "../constants/v2/endPoints";
import { isImpersonatedToken } from "./impersonationHelper";

const GITCHA_APP_DOMAIN = process.env.REACT_APP_GITCHA_APP_DOMAIN;
const BLS_ENV_URLS = process.env.REACT_APP_BLS_ENV_URLS;
const CROSS_LOGIN_ORIGIN_KEY = "crossLoginOrigin";

export const SSO_PROVIDERS = ["saml", "spark", "cross-login"];

const stripWww = (host) => host.replace(/^www\./, "");

const getAllowedHosts = () => {
  const hosts = [window.location.host];
  try {
    if (GITCHA_APP_DOMAIN) hosts.push(stripWww(new URL(GITCHA_APP_DOMAIN).host));
  } catch {}
  if (BLS_ENV_URLS) {
    BLS_ENV_URLS.split(",").forEach((url) => {
      try {
        const trimmed = url.trim();
        if (trimmed) hosts.push(stripWww(new URL(trimmed).host));
      } catch {}
    });
  }
  return hosts.filter(Boolean);
};

export const isAllowedRedirectUrl = (url) => {
  try {
    const parsed = new URL(url);
    const allowed = getAllowedHosts();
    return allowed.some((host) => parsed.host === host);
  } catch {
    return false;
  }
};

export const isSSOLogin = () => {
  const userInfo = getLocalStorage(USER_INFO_KEY);
  const provider = userInfo?.token?.providerLogin;
  return SSO_PROVIDERS.includes(provider);
};

export const saveCrossLoginOrigin = (returnUrl) => {
  try {
    const origin = new URL(returnUrl).origin;
    setLocalStorage(CROSS_LOGIN_ORIGIN_KEY, origin);
  } catch (e) {
    // invalid URL
  }
};

const isOnGitchaDomain = () => {
  if (!GITCHA_APP_DOMAIN) return false;
  const gitchaHost = new URL(GITCHA_APP_DOMAIN).host;
  return window.location.host === gitchaHost;
};

export const getOtherDomain = () => {
  if (isOnGitchaDomain()) {
    const origin = getLocalStorage(CROSS_LOGIN_ORIGIN_KEY);
    return origin && isAllowedRedirectUrl(origin) ? origin : null;
  }
  return GITCHA_APP_DOMAIN || null;
};

export const triggerCrossLogin = async (returnUrl) => {
  if (!GITCHA_APP_DOMAIN) return false;
  if (!isSSOLogin()) return false;

  const userInfo = getLocalStorage(USER_INFO_KEY);
  const accessToken = userInfo?.token?.accessToken;
  if (!accessToken) return false;
  // An impersonated session can be tagged with the same providerLogin as
  // a real cross-login exchange (see CrossLoginCallback.js), so isSSOLogin
  // alone can't tell them apart. This handoff is built for the real
  // app<->BLS domain bounce - running it for an impersonated session could
  // log the admin out on the other domain, or bounce them through a
  // cross-login flow never granted to the impersonated user.
  if (isImpersonatedToken(accessToken)) return false;

  try {
    const response = await apiCall(
      endPoints.CROSS_LOGIN_GENERATE,
      "POST",
      null,
      null,
      accessToken
    );
    const crossLoginToken = response?.result?.crossLoginToken;
    if (crossLoginToken) {
      const crossLoginUrl = `${GITCHA_APP_DOMAIN}/auth/cross-login?token=${encodeURIComponent(crossLoginToken)}&returnUrl=${encodeURIComponent(returnUrl)}`;
      window.location.href = crossLoginUrl;
      return true;
    }
  } catch (e) {
    // Cross-login failed silently - user is still logged in on the current domain
  }
  return false;
};

export const triggerCrossLogout = (returnUrl, otherDomain) => {
  // Same reasoning as triggerCrossLogin above - an impersonated session
  // must never be bounced through the real app<->BLS domain handoff.
  const userInfo = getLocalStorage(USER_INFO_KEY);
  if (isImpersonatedToken(userInfo?.token?.accessToken)) return false;

  otherDomain = otherDomain || getOtherDomain();
  if (!otherDomain) return false;

  const crossLogoutUrl = `${otherDomain}/auth/cross-logout?returnUrl=${encodeURIComponent(returnUrl)}`;
  window.location.href = crossLogoutUrl;
  return true;
};
